import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  Plus, CheckCircle2, XCircle, PauseCircle, Clock, AlertTriangle,
} from "lucide-react";
import Modal from "../components/Modal";
import WarehouseTabs from "../components/WarehouseTabs";
import SearchableSelect from "../components/SearchableSelect";
import { Button } from "../components/ui/button";
import { FormError } from "../components/ui/feedback";
import {
  DataValue,
  PassiveBadge,
  PrimarySecondaryValue,
  formatPresentationTimestamp,
} from "../components/ui/data-presentation";
import {
  ListPagination,
  ListTable,
  ListTableShell,
  ListTableRefreshingRow,
  ListTableState,
  ListTableViewport,
} from "../components/ui/list-table";
import {
  ClearAllFilters,
  ColumnDateFilter,
  ColumnFilterCell,
  ColumnFilterRow,
  ColumnNumericFilter,
  ColumnSelectFilter,
  ColumnTextFilter,
  DateRangeSelector,
  matchesDateRange,
} from "../components/ui/search-filter";
import { OrdinaryPageBody, OrdinaryPageHeader } from "../components/ui/page-structure";
import { RefreshButton } from "../components/ui/refresh-button";

const AGING_THRESHOLD_DAYS = 5;
const MS_PER_DAY = 86400000;
const EMPTY_DATE_RANGE = Object.freeze({ from: "", to: "" });

const HOLD_ACTIVE_TABLE_GEOMETRY = Object.freeze([
  { key: "date", kind: "fixed", width: 150 },
  { key: "age", kind: "fixed", width: 84 },
  { key: "model", kind: "fixed", width: 170 },
  { key: "part", kind: "fill", minWidth: 280 },
  { key: "quantity", kind: "fixed", width: 80 },
  { key: "by", kind: "fixed", width: 190 },
  { key: "notes", kind: "fixed", width: 220 },
  { key: "actions", kind: "utility", width: 104 },
]);

const HOLD_RESOLVED_TABLE_GEOMETRY = Object.freeze([
  { key: "date", kind: "fixed", width: 150 },
  { key: "model", kind: "fixed", width: 170 },
  { key: "part", kind: "fixed", width: 280 },
  { key: "quantity", kind: "fixed", width: 80 },
  { key: "status", kind: "fixed", width: 110 },
  { key: "serial-number", kind: "fixed", width: 140 },
  { key: "customer", kind: "fill", minWidth: 220 },
  { key: "by", kind: "fixed", width: 190 },
]);

const EMPTY_ACTIVE_FILTERS = Object.freeze({ date: "", age: "", model: "", part: "", quantity: "", by: "", notes: "" });
const EMPTY_RESOLVED_FILTERS = Object.freeze({ date: "", model: "", part: "", quantity: "", status: "", serialNumber: "", customer: "", by: "" });

function includesText(value, query) {
  return String(value || "").toLowerCase().includes(String(query || "").trim().toLowerCase());
}

function itemMatchesText(hold, query) {
  return includesText(hold.part_number, query) || includesText(hold.part_name, query);
}

function matchesExactNumber(value, query) {
  return query === "" || Number(value) === Number(query);
}

function daysSince(iso) {
  return Math.floor((Date.now() - new Date(iso).getTime()) / MS_PER_DAY);
}

export default function Hold() {
  const { warehouses } = useAuth();
  const [items, setItems] = useState([]);
  const [holds, setHolds] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState("");

  const [activeWh, setActiveWh] = useState(null);

  // Page-owned filter drafts and applied constraints.
  const [activeFilterDrafts, setActiveFilterDrafts] = useState(EMPTY_ACTIVE_FILTERS);
  const [activeFilters, setActiveFilters] = useState(EMPTY_ACTIVE_FILTERS);
  const [resolvedFilterDrafts, setResolvedFilterDrafts] = useState(EMPTY_RESOLVED_FILTERS);
  const [resolvedFilters, setResolvedFilters] = useState(EMPTY_RESOLVED_FILTERS);
  const [dateRangeDraft, setDateRangeDraft] = useState(EMPTY_DATE_RANGE);
  const [dateRange, setDateRange] = useState(EMPTY_DATE_RANGE);

  const [form, setForm] = useState({ model: "", item_id: "", warehouse_id: "", quantity: 1, notes: "" });

  const [useOpen, setUseOpen] = useState(false);
  const [useForm, setUseForm] = useState({ hold_id: "", serial_number: "", customer_name: "", notes: "" });
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [useSubmitting, setUseSubmitting] = useState(false);
  const [useError, setUseError] = useState("");
  const [cancelSubmitting, setCancelSubmitting] = useState(false);
  const [cancelError, setCancelError] = useState("");
  const [notesPopover, setNotesPopover] = useState(null);
  const [activePage, setActivePage] = useState(1);
  const [resolvedPage, setResolvedPage] = useState(1);
  const [activePageSize, setActivePageSize] = useState(10);
  const [resolvedPageSize, setResolvedPageSize] = useState(10);

  useEffect(() => {
    if (!notesPopover) return;
    const close = () => setNotesPopover(null);
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [notesPopover]);

  useEffect(() => {
    if (!activeWh && warehouses.length > 0) setActiveWh(warehouses[0].id);
  }, [warehouses, activeWh]);

  const load = async () => {
    setListLoading(true);
    setListError("");
    try {
      const [i, h] = await Promise.all([api.get("/items"), api.get("/holds")]);
      setItems(i.data);
      setHolds(h.data);
    } catch (requestError) {
      setListError(formatApiError(requestError));
    } finally {
      setListLoading(false);
    }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { setActivePage(1); setResolvedPage(1); }, [activeWh]);

  const stockAt = (itemId, whId) => items.find((i) => i.id === itemId)?.stock?.[whId] ?? 0;

  // Filtered by active warehouse + filters
  const tabCounts = useMemo(() => {
    const c = {};
    warehouses.forEach((w) => {
      c[w.id] = holds.filter((h) => h.warehouse_id === w.id && h.status === "active").length;
    });
    return c;
  }, [holds, warehouses]);

  const active = useMemo(() => holds.filter((h) => {
    if (activeWh && h.warehouse_id !== activeWh) return false;
    if (h.status !== "active") return false;
    if (!matchesDateRange(String(h.created_at || "").slice(0, 10), dateRange)) return false;
    const fullItem = items.find((item) => item.id === h.item_id);
    if (activeFilters.date && String(h.created_at || "").slice(0, 10) !== activeFilters.date) return false;
    if (!matchesExactNumber(daysSince(h.created_at), activeFilters.age)) return false;
    if (activeFilters.model && !includesText(fullItem?.model, activeFilters.model)) return false;
    if (activeFilters.part && !itemMatchesText(h, activeFilters.part)) return false;
    if (!matchesExactNumber(h.quantity, activeFilters.quantity)) return false;
    if (activeFilters.by && !includesText(h.created_by_email, activeFilters.by)) return false;
    if (activeFilters.notes && !includesText(h.notes, activeFilters.notes)) return false;
    return true;
  }), [activeFilters, activeWh, dateRange, holds, items]);

  const resolved = useMemo(() => holds.filter((h) => {
    if (activeWh && h.warehouse_id !== activeWh) return false;
    if (h.status === "active") return false;
    const resolvedDate = String(h.resolved_at || h.created_at || "").slice(0, 10);
    if (!matchesDateRange(resolvedDate, dateRange)) return false;
    const fullItem = items.find((item) => item.id === h.item_id);
    if (resolvedFilters.date && resolvedDate !== resolvedFilters.date) return false;
    if (resolvedFilters.model && !includesText(fullItem?.model, resolvedFilters.model)) return false;
    if (resolvedFilters.part && !itemMatchesText(h, resolvedFilters.part)) return false;
    if (!matchesExactNumber(h.quantity, resolvedFilters.quantity)) return false;
    if (resolvedFilters.status && h.status !== resolvedFilters.status) return false;
    if (resolvedFilters.serialNumber && !includesText(h.serial_number, resolvedFilters.serialNumber)) return false;
    if (resolvedFilters.customer && !includesText(h.customer_name, resolvedFilters.customer)) return false;
    if (resolvedFilters.by && !includesText(h.created_by_email, resolvedFilters.by)) return false;
    return true;
  }).sort((a, b) => new Date(b.resolved_at || b.created_at) - new Date(a.resolved_at || a.created_at)), [activeWh, dateRange, holds, items, resolvedFilters]);

  const activeTotalPages = Math.ceil(active.length / activePageSize);
  const resolvedTotalPages = Math.ceil(resolved.length / resolvedPageSize);
  const activePageItems = active.slice((activePage - 1) * activePageSize, activePage * activePageSize);
  const resolvedPageItems = resolved.slice((resolvedPage - 1) * resolvedPageSize, resolvedPage * resolvedPageSize);

  useEffect(() => {
    const lastPage = Math.max(activeTotalPages, 1);
    if (activePage > lastPage) setActivePage(lastPage);
  }, [activePage, activeTotalPages]);
  useEffect(() => {
    const lastPage = Math.max(resolvedTotalPages, 1);
    if (resolvedPage > lastPage) setResolvedPage(lastPage);
  }, [resolvedPage, resolvedTotalPages]);

  const agingCount = active.filter((h) => daysSince(h.created_at) > AGING_THRESHOLD_DAYS).length;

  const openUse = (hold) => {
    setUseForm({ hold_id: hold.id, serial_number: "", customer_name: "", notes: "" });
    setUseError("");
    setUseOpen(true);
  };

  const submitUse = async () => {
    if (!useForm.serial_number || !useForm.customer_name) {
      setUseError("Serial # and customer name are required");
      return;
    }
    setUseSubmitting(true);
    setUseError("");
    try {
      await api.post(`/holds/${useForm.hold_id}/use`, {
        serial_number: useForm.serial_number,
        customer_name: useForm.customer_name,
        notes: useForm.notes,
      });
      toast.success("Saved to spare part usage");
      setUseOpen(false);
      load();
    } catch (e) {
      setUseError(formatApiError(e));
    } finally {
      setUseSubmitting(false);
    }
  };

  const cancelHold = (hold) => {
    setCancelTarget(hold);
    setCancelError("");
    setCancelOpen(true);
  };

  const confirmCancel = async () => {
    setCancelSubmitting(true);
    setCancelError("");
    try {
      await api.post(`/holds/${cancelTarget.id}/cancel`);
      toast.success("Hold cancelled, stock returned");
      setCancelOpen(false);
      setCancelTarget(null);
      load();
    } catch (e) {
      setCancelError(formatApiError(e));
    } finally {
      setCancelSubmitting(false);
    }
  };
 
  const activeWhObj = warehouses.find((w) => w.id === activeWh);
  const hasActiveFilters = Object.values(activeFilterDrafts).some(Boolean) || dateRange.from || dateRange.to;
  const applyActiveFilter = (name, value) => {
    setActiveFilters((current) => ({ ...current, [name]: value }));
    setActivePage(1);
  };
  const applyResolvedFilter = (name, value) => {
    setResolvedFilters((current) => ({ ...current, [name]: value }));
    setResolvedPage(1);
  };
  const applyDateRange = (value) => {
    setDateRange(value);
    setActivePage(1);
    setResolvedPage(1);
  };
  const clearActiveFilters = () => {
    setActiveFilterDrafts(EMPTY_ACTIVE_FILTERS);
    setActiveFilters(EMPTY_ACTIVE_FILTERS);
    setDateRangeDraft(EMPTY_DATE_RANGE);
    setDateRange(EMPTY_DATE_RANGE);
    setActivePage(1);
    setResolvedPage(1);
  };

  return (
    <OrdinaryPageBody data-testid="hold-page">
      <OrdinaryPageHeader
        title="Hold"
        actions={<RefreshButton onClick={load} loading={listLoading} accessibleLabel="Refresh holds" data-testid="hold-refresh" />}
        data-testid="hold-page-header"
      />

      {/* Warehouse Tabs + content */}
      <ListTableShell>
        <div className="flex min-w-0 flex-wrap items-end gap-3 border-b border-zinc-200" data-testid="hold-branch-warning-row">
          <div className="min-w-0">
            <WarehouseTabs
              warehouses={warehouses}
              active={activeWh}
              onChange={setActiveWh}
              counts={tabCounts}
              testidPrefix="hold-tab"
              className="border-b-0"
            />
          </div>
          {agingCount > 0 && (
            <div
              className="mb-2 flex items-center gap-2 rounded-sm border border-destructive/30 bg-semantic-danger px-3 py-2.5 text-xs font-medium text-semantic-danger"
              data-testid="hold-aging-warning"
            >
              <AlertTriangle className="w-3.5 h-3.5" />
              {agingCount} hold{agingCount > 1 ? "s" : ""} older than {AGING_THRESHOLD_DAYS} days
            </div>
          )}
          <DateRangeSelector
            value={dateRangeDraft}
            onChange={setDateRangeDraft}
            onApply={applyDateRange}
            mode="immediate"
            id="hold-date-range"
            label={null}
            controlsClassName="h-9"
            className="ml-auto mb-2 mr-5 w-[170px] shrink-0"
          />
        </div>

        {/* Active holds */}
        <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between">
          <div>
            <div className="text-[10px] tracking-[0.15em] uppercase text-zinc-500 font-semibold">Active</div>
            <div className="text-base font-semibold tracking-tight flex items-center gap-2">
              Holds — {activeWhObj?.name || ""} <PauseCircle className="w-4 h-4 text-semantic-info" />
            </div>
          </div>
        </div>
        <ListTableViewport>
        <ListTable geometry={HOLD_ACTIVE_TABLE_GEOMETRY} data-testid="hold-active-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Age</th>
              <th>Model</th>
              <th>Part</th>
              <th className="text-right">Qty</th>
              <th>By</th>
              <th>Notes</th>
              <th className="text-right">Actions</th>
            </tr>
            <ColumnFilterRow className="bg-white">
              <ColumnFilterCell><ColumnDateFilter value={activeFilterDrafts.date} onValueChange={(value) => setActiveFilterDrafts((current) => ({ ...current, date: value }))} onApply={(value) => applyActiveFilter("date", value)} aria-label="Filter active hold date" data-testid="hold-active-filter-date" /></ColumnFilterCell>
              <ColumnFilterCell><ColumnNumericFilter value={activeFilterDrafts.age} onValueChange={(value) => setActiveFilterDrafts((current) => ({ ...current, age: value }))} onApply={(value) => applyActiveFilter("age", value)} allowDecimal={false} allowNegative={false} placeholder="Age" aria-label="Filter active hold age" data-testid="hold-active-filter-age" /></ColumnFilterCell>
              <ColumnFilterCell><ColumnTextFilter value={activeFilterDrafts.model} onValueChange={(value) => setActiveFilterDrafts((current) => ({ ...current, model: value }))} onApply={(value) => applyActiveFilter("model", value)} placeholder="Filter…" aria-label="Filter active hold model" data-testid="hold-active-filter-model" /></ColumnFilterCell>
              <ColumnFilterCell><ColumnTextFilter value={activeFilterDrafts.part} onValueChange={(value) => setActiveFilterDrafts((current) => ({ ...current, part: value }))} onApply={(value) => applyActiveFilter("part", value)} placeholder="Filter…" aria-label="Filter active hold part" data-testid="hold-active-filter-part" /></ColumnFilterCell>
              <ColumnFilterCell><ColumnNumericFilter value={activeFilterDrafts.quantity} onValueChange={(value) => setActiveFilterDrafts((current) => ({ ...current, quantity: value }))} onApply={(value) => applyActiveFilter("quantity", value)} allowDecimal={false} allowNegative={false} placeholder="Qty" aria-label="Filter active hold quantity" data-testid="hold-active-filter-quantity" /></ColumnFilterCell>
              <ColumnFilterCell><ColumnTextFilter value={activeFilterDrafts.by} onValueChange={(value) => setActiveFilterDrafts((current) => ({ ...current, by: value }))} onApply={(value) => applyActiveFilter("by", value)} placeholder="Filter…" aria-label="Filter active hold creator" data-testid="hold-active-filter-by" /></ColumnFilterCell>
              <ColumnFilterCell><ColumnTextFilter value={activeFilterDrafts.notes} onValueChange={(value) => setActiveFilterDrafts((current) => ({ ...current, notes: value }))} onApply={(value) => applyActiveFilter("notes", value)} placeholder="Filter…" aria-label="Filter active hold notes" data-testid="hold-active-filter-notes" /></ColumnFilterCell>
              <ColumnFilterCell aria-label="Actions filter controls">
                <div className="flex h-7 items-center justify-end">
                  {hasActiveFilters ? <ClearAllFilters onClick={clearActiveFilters} data-testid="hold-active-filter-clear-all" /> : null}
                </div>
              </ColumnFilterCell>
            </ColumnFilterRow>
          </thead>
          <tbody>
            <ListTableRefreshingRow active={listLoading && active.length > 0} colSpan={8} />
            {listLoading && active.length === 0 ? (
              <ListTableState colSpan={8} kind="loading">Loading active holds...</ListTableState>
            ) : listError ? (
              <ListTableState colSpan={8} kind="error" onRetry={load}>{listError}</ListTableState>
            ) : active.length === 0 ? (
              <ListTableState colSpan={8}>No active holds for this warehouse.</ListTableState>
            ) : activePageItems.map((h) => {
              const days = daysSince(h.created_at);
              const aging = days > AGING_THRESHOLD_DAYS;
              const fullItem = items.find((i) => i.id === h.item_id);
              return (
                <tr
                  key={h.id}
                  data-testid={`hold-row-${h.id}`}
                  className={aging ? "bg-destructive/10" : ""}
                >
                  <td className="whitespace-nowrap text-xs font-medium tabular-nums">{formatPresentationTimestamp(h.created_at)}</td>
                  <td>
                    <PassiveBadge
                      tone={aging ? "danger" : "neutral"}
                      className="gap-1"
                      data-testid={aging ? `hold-aging-badge-${h.id}` : undefined}
                    >
                      <Clock className="w-2.5 h-2.5" />
                      {days}d
                    </PassiveBadge>
                  </td>
                  <td className="text-xs font-medium">
                    <DataValue value={fullItem?.model} />
                    {(fullItem?.compatible_models || []).filter((m) => m !== fullItem?.model).length > 0 && (
                      <div className="text-[10px] text-zinc-400">
                        {fullItem.compatible_models.filter((m) => m !== fullItem.model).join(", ")}
                      </div>
                    )}
                  </td>
                  <td>
                    <PrimarySecondaryValue primary={h.part_name} secondary={h.part_number} primaryClassName="text-xs" secondaryClassName="font-semibold tracking-[0.02em]" />
                  </td>
                  <td className="num">{h.quantity}</td>
                  <td className="max-w-[180px] text-xs"><DataValue value={h.created_by_email} containment="truncate" /></td>
                  <td className="text-xs text-zinc-500" style={{whiteSpace:"nowrap"}}>
                    {!h.notes ? "—" : (() => {
                      const isLong = h.notes.length > 25;
                      return (
                        <div className="relative" style={{display:"inline-block"}}>
                          {isLong ? (
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); setNotesPopover(notesPopover === h.id ? null : h.id); }}
                              className="text-left text-zinc-500 hover:text-zinc-700 underline decoration-dotted underline-offset-2"
                              data-action-category="utility"
                            >
                              {h.notes.slice(0, 25)}…
                            </button>
                          ) : h.notes}
                          {notesPopover === h.id && (
                            <div
                              className="absolute z-20 left-0 bottom-full mb-1 bg-white border border-zinc-200 rounded-lg shadow-brutal p-3 text-xs text-zinc-700 whitespace-pre-wrap"
                              style={{minWidth:"200px", maxWidth:"280px"}}
                              onMouseDown={(e) => e.stopPropagation()}
                            >
                              {h.notes}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </td>
                  <td>
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        type="button"
                        onClick={() => openUse(h)}
                        variant="success"
                        size="row-icon"
                        title="Use held item"
                        aria-label={`Use held item ${h.part_number}`}
                        data-action-category="submit-confirm"
                        data-testid={`hold-use-btn-${h.id}`}
                      >
                        <CheckCircle2 strokeWidth={2} />
                      </Button>
                      <Button
                        type="button"
                        onClick={() => cancelHold(h)}
                        variant="cancel"
                        size="row-icon"
                        title="Cancel hold"
                        aria-label={`Cancel hold for ${h.part_number}`}
                        data-action-category="cancel"
                        data-testid={`hold-cancel-btn-${h.id}`}
                      >
                        <XCircle strokeWidth={2} />
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </ListTable>
        </ListTableViewport>

        <ListPagination
          page={activePage}
          pageSize={activePageSize}
          total={active.length}
          totalPages={activeTotalPages}
          loading={listLoading}
          onPrevious={() => setActivePage((current) => Math.max(1, current - 1))}
          onNext={() => setActivePage((current) => Math.min(activeTotalPages, current + 1))}
          onPageSizeChange={(nextPageSize) => { setActivePageSize(nextPageSize); setActivePage(1); }}
          pageSizeAriaLabel="Active holds rows per page"
          summaryTestId="hold-active-pagination-summary"
        />

        {/* Resolved */}
        <>
            <div className="px-6 py-4 border-y border-zinc-200 bg-zinc-50">
              <div className="text-[10px] tracking-[0.15em] uppercase text-zinc-500 font-semibold">History</div>
              <div className="text-base font-semibold tracking-tight">Resolved Holds</div>
            </div>
            <ListTableViewport>
            <ListTable geometry={HOLD_RESOLVED_TABLE_GEOMETRY} data-testid="hold-resolved-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Model</th>
                  <th>Part</th>
                  <th className="text-right">Qty</th>
                  <th>Status</th>
                  <th>SN</th>
                  <th>Customer</th>
                  <th>By</th>
                </tr>
                <ColumnFilterRow className="bg-white">
                  <ColumnFilterCell><ColumnDateFilter value={resolvedFilterDrafts.date} onValueChange={(value) => setResolvedFilterDrafts((current) => ({ ...current, date: value }))} onApply={(value) => applyResolvedFilter("date", value)} aria-label="Filter resolved hold date" data-testid="hold-resolved-filter-date" /></ColumnFilterCell>
                  <ColumnFilterCell><ColumnTextFilter value={resolvedFilterDrafts.model} onValueChange={(value) => setResolvedFilterDrafts((current) => ({ ...current, model: value }))} onApply={(value) => applyResolvedFilter("model", value)} placeholder="Filter…" aria-label="Filter resolved hold model" data-testid="hold-resolved-filter-model" /></ColumnFilterCell>
                  <ColumnFilterCell><ColumnTextFilter value={resolvedFilterDrafts.part} onValueChange={(value) => setResolvedFilterDrafts((current) => ({ ...current, part: value }))} onApply={(value) => applyResolvedFilter("part", value)} placeholder="Filter…" aria-label="Filter resolved hold part" data-testid="hold-resolved-filter-part" /></ColumnFilterCell>
                  <ColumnFilterCell><ColumnNumericFilter value={resolvedFilterDrafts.quantity} onValueChange={(value) => setResolvedFilterDrafts((current) => ({ ...current, quantity: value }))} onApply={(value) => applyResolvedFilter("quantity", value)} allowDecimal={false} allowNegative={false} placeholder="Qty" aria-label="Filter resolved hold quantity" data-testid="hold-resolved-filter-quantity" /></ColumnFilterCell>
                  <ColumnFilterCell><ColumnSelectFilter value={resolvedFilterDrafts.status} onValueChange={(value) => setResolvedFilterDrafts((current) => ({ ...current, status: value }))} onApply={(value) => applyResolvedFilter("status", value)} placeholder="All" options={[{ value: "used", label: "Used" }, { value: "cancelled", label: "Cancelled" }]} aria-label="Filter resolved hold status" data-testid="hold-resolved-filter-status" /></ColumnFilterCell>
                  <ColumnFilterCell><ColumnTextFilter value={resolvedFilterDrafts.serialNumber} onValueChange={(value) => setResolvedFilterDrafts((current) => ({ ...current, serialNumber: value }))} onApply={(value) => applyResolvedFilter("serialNumber", value)} placeholder="Filter…" aria-label="Filter resolved hold serial number" data-testid="hold-resolved-filter-serial-number" /></ColumnFilterCell>
                  <ColumnFilterCell><ColumnTextFilter value={resolvedFilterDrafts.customer} onValueChange={(value) => setResolvedFilterDrafts((current) => ({ ...current, customer: value }))} onApply={(value) => applyResolvedFilter("customer", value)} placeholder="Filter…" aria-label="Filter resolved hold customer" data-testid="hold-resolved-filter-customer" /></ColumnFilterCell>
                  <ColumnFilterCell><ColumnTextFilter value={resolvedFilterDrafts.by} onValueChange={(value) => setResolvedFilterDrafts((current) => ({ ...current, by: value }))} onApply={(value) => applyResolvedFilter("by", value)} placeholder="Filter…" aria-label="Filter resolved hold creator" data-testid="hold-resolved-filter-by" /></ColumnFilterCell>
                </ColumnFilterRow>
              </thead>
              <tbody>
                <ListTableRefreshingRow active={listLoading && resolved.length > 0} colSpan={8} />
                {listLoading && resolved.length === 0 ? (
                  <ListTableState colSpan={8} kind="loading">Loading resolved holds...</ListTableState>
                ) : listError ? (
                  <ListTableState colSpan={8} kind="error" onRetry={load}>{listError}</ListTableState>
                ) : resolved.length === 0 ? (
                  <ListTableState colSpan={8}>No resolved holds for this warehouse.</ListTableState>
                ) : resolvedPageItems.map((h) => {
                  const fullItem = items.find((i) => i.id === h.item_id);
                  return (
                  <tr key={h.id}>
                    <td className="whitespace-nowrap text-xs font-medium tabular-nums">{formatPresentationTimestamp(h.resolved_at || h.created_at)}</td>
                    <td className="text-xs font-medium">
                      <DataValue value={fullItem?.model} />
                      {(fullItem?.compatible_models || []).filter((m) => m !== fullItem?.model).length > 0 && (
                        <div className="text-[10px] text-zinc-400">
                          {fullItem.compatible_models.filter((m) => m !== fullItem.model).join(", ")}
                        </div>
                      )}
                    </td>
                    <td>
                      <PrimarySecondaryValue primary={h.part_name} secondary={h.part_number} primaryClassName="text-xs" secondaryClassName="font-semibold tracking-[0.02em]" />
                    </td>
                    <td className="num">{h.quantity}</td>
                    <td>
                      <PassiveBadge tone={h.status === "used" ? "success" : "neutral"}>
                        {h.status.toUpperCase()}
                      </PassiveBadge>
                    </td>                    
                    <td className="max-w-[110px] text-xs font-medium tracking-[0.02em]"><DataValue value={h.serial_number} containment="truncate" /></td>
                    <td className="max-w-[200px] text-xs"><DataValue value={h.customer_name} containment="truncate" /></td>
                    <td className="max-w-[150px] text-xs text-zinc-500"><DataValue value={h.created_by_email} containment="truncate" /></td>
                  </tr>
                  );
                })}
              </tbody>
            </ListTable>
            </ListTableViewport>
            <ListPagination
              page={resolvedPage}
              pageSize={resolvedPageSize}
              total={resolved.length}
              totalPages={resolvedTotalPages}
              loading={listLoading}
              onPrevious={() => setResolvedPage((current) => Math.max(1, current - 1))}
              onNext={() => setResolvedPage((current) => Math.min(resolvedTotalPages, current + 1))}
              onPageSizeChange={(nextPageSize) => { setResolvedPageSize(nextPageSize); setResolvedPage(1); }}
              pageSizeAriaLabel="Resolved holds rows per page"
              summaryTestId="hold-resolved-pagination-summary"
            />
          </>
      </ListTableShell>

      {/* Cancel confirm modal */}
      {cancelOpen && cancelTarget && (
        <Modal title="Cancel Hold" onClose={() => setCancelOpen(false)} busy={cancelSubmitting} testid="hold-cancel-modal">
          <div className="space-y-4">
            <p className="text-sm text-zinc-700">
              Cancel hold on <span className="font-semibold">{cancelTarget.part_number}</span>? Stock will be returned.
            </p>
            <FormError>{cancelError}</FormError>
            <div className="flex justify-end gap-2 pt-3 border-t border-zinc-200">
              <Button type="button" onClick={() => setCancelOpen(false)} variant="navigation-back" data-action-category="navigation-back">Back</Button>
              <Button type="button" onClick={confirmCancel} loading={cancelSubmitting} loadingLabel="Cancelling..." variant="cancel" data-action-category="cancel" data-testid="hold-cancel-confirm">Confirm Cancel</Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Use modal */}
      {useOpen && (
        <Modal title="Use held item" onClose={() => setUseOpen(false)} busy={useSubmitting} testid="hold-use-modal">
          <div className="space-y-4">
            <div>
              <div className="text-[10px] tracking-[0.1em] uppercase font-semibold text-zinc-700 mb-1.5">Serial Number *</div>
              <input value={useForm.serial_number} onChange={(e) => setUseForm({ ...useForm, serial_number: e.target.value })} className="wms-input" data-testid="hold-use-sn-input" placeholder="SN-XXXXXX" />
            </div>
            <FormError>{useError}</FormError>
            <div>
              <div className="text-[10px] tracking-[0.1em] uppercase font-semibold text-zinc-700 mb-1.5">Customer Name *</div>
              <input value={useForm.customer_name} onChange={(e) => setUseForm({ ...useForm, customer_name: e.target.value })} className="wms-input" data-testid="hold-use-customer-input" placeholder="Full customer name" />
            </div>
            <div>
              <div className="text-[10px] tracking-[0.1em] uppercase font-semibold text-zinc-700 mb-1.5">Notes</div>
              <input value={useForm.notes} onChange={(e) => setUseForm({ ...useForm, notes: e.target.value })} className="wms-input" data-testid="hold-use-notes-input" />
            </div>
            <div className="flex justify-end gap-2 pt-3 border-t border-zinc-200">
              <Button type="button" onClick={() => setUseOpen(false)} variant="navigation-back" data-action-category="navigation-back">Back</Button>
              <Button type="button" onClick={submitUse} loading={useSubmitting} loadingLabel="Saving..." variant="success" data-action-category="submit-confirm" data-testid="hold-use-submit">Save usage</Button>
            </div>
          </div>
        </Modal>
      )}
    </OrdinaryPageBody>
  );
}
